import { useAuthenticator } from '@aws-amplify/ui-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { selectAuthRefreshTokenExpiresAt } from 'src/redux/auth/selectors';
import { AuthToken } from './types';

interface AuthTokenResult {
  /** The user's current auth token, if authenticated. */
  value?: AuthToken;
  /** Whether or not the session is expired. If undefined, no session exists. */
  isExpired?: boolean;
  /** Whether or not the user is authenticated, regardless of whether or not the session is valid. */
  isAuthenticated: boolean;
}

/**
 * A hook that returns the user's current auth tokens (access and refresh), if the user is authenticated.
 */
export const useAuthToken = (): AuthTokenResult => {
  const [currentDateTime, setCurrentDateTime] = React.useState(Date.now());
  const { user } = useAuthenticator(s => [s.user]);
  const refreshExp = useSelector(selectAuthRefreshTokenExpiresAt);

  // An effect that causes the current datetime to update on an interval, so the `isExpired` flag can be recomputed.
  React.useEffect(() => {
    const intervalHandle = window.setInterval(() => {
      setCurrentDateTime(Date.now());
    }, 1000 * 60);

    return () => {
      window.clearInterval(intervalHandle);
    };
  }, []);

  const cognitoUserSession = React.useMemo(() => user?.getSignInUserSession(), [user]);
  const cognitoAccessToken = React.useMemo(
    () => cognitoUserSession?.getAccessToken().getJwtToken(),
    [cognitoUserSession],
  );
  const cognitoAccessTokenExp = React.useMemo(
    () => cognitoUserSession?.getAccessToken().getExpiration(),
    [cognitoUserSession],
  );
  const cognitoAccessTokenIss = React.useMemo(
    () => cognitoUserSession?.getAccessToken().getIssuedAt(),
    [cognitoUserSession],
  );
  const cognitoRefreshToken = React.useMemo(
    () => cognitoUserSession?.getRefreshToken().getToken(),
    [cognitoUserSession],
  );

  const value = React.useMemo(() => {
    if (
      cognitoAccessToken === undefined ||
      cognitoAccessTokenExp === undefined ||
      cognitoRefreshToken === undefined ||
      cognitoAccessTokenIss === undefined
    ) {
      return;
    }

    return {
      access: {
        token: cognitoAccessToken,
        expiresAt: cognitoAccessTokenExp * 1000,
        issuedAt: cognitoAccessTokenIss * 1000,
      },
      refresh: { token: cognitoRefreshToken },
    };
  }, [cognitoAccessToken, cognitoAccessTokenExp, cognitoAccessTokenIss, cognitoRefreshToken]);

  const isAuthenticated = React.useMemo<boolean>(() => {
    if (refreshExp === undefined) return false;
    return currentDateTime < refreshExp;
  }, [currentDateTime, refreshExp]);

  const isExpired = React.useMemo<boolean | undefined>(() => {
    if (isAuthenticated === false) return undefined;
    if (cognitoAccessTokenExp === undefined) return true;
    return cognitoAccessTokenExp * 1000 < currentDateTime;
  }, [cognitoAccessTokenExp, currentDateTime, isAuthenticated]);

  const result = React.useMemo<AuthTokenResult>(() => {
    const _result: AuthTokenResult = { value, isExpired, isAuthenticated };
    return _result;
  }, [isAuthenticated, isExpired, value]);

  return result;
};
